/**
 * VoiceMode.js — Chorus "Car Mode" Voice Interface
 * 
 * Turn-based voice chat with premium UI:
 * - Web Speech API for STT (speech-to-text)
 * - Browser SpeechSynthesis for TTS (text-to-speech), MiniMax upgrade path
 * - Canvas waveform visualization with real audio analyser
 * - State machine: idle → listening → processing → speaking
 * - Auto-continue: starts listening again after agent speaks
 * - Tap to interrupt
 */

import { sendMessage, sendAgentMessage } from '../lib/chat.js';
import { store } from '../lib/appState.js';
import { API_BASE } from '../lib/api.js';

// ─── State Machine ───
const STATES = {
  IDLE: 'idle',
  LISTENING: 'listening', 
  PROCESSING: 'processing',
  SPEAKING: 'speaking',
};

let voiceState = {
  active: false,
  state: STATES.IDLE,
  transcript: '',
  liveTranscript: '',
  agentResponse: '',
  history: [], // { role, text }
  error: null,
};

// ─── Audio Context + Analyser ───
let audioCtx = null;
let analyser = null;
let micStream = null;
let micSource = null;
let animationFrame = null;
let canvas = null;
let canvasCtx = null;

// ─── Speech Recognition ───
let recognition = null;
let recognitionTimeout = null;

// ─── Speech Synthesis ───
let currentUtterance = null;

// ─── Callbacks ───
let onStateChange = null;

function getActiveAgentTab() {
  const state = store.getState();
  return state.ui?.activeTab || null;
}

function isAgentTab(tabId) {
  return tabId && tabId.includes(':');
}

// ─── Audio Setup ───
async function setupAudio() {
  if (audioCtx) return;
  
  try {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    analyser = audioCtx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;
  } catch (err) {
    vlog(`AudioContext failed: ${err.message}`);
    return;
  }
  
  // Don't request getUserMedia — it conflicts with SpeechRecognition on Android
  // The waveform will show idle animation without real audio data
  // This is the trade-off: visualization is decorative, but speech recognition works
  vlog('Audio context ready (waveform decorative only)');
}

function teardownAudio() {
  if (micStream) {
    micStream.getTracks().forEach(t => t.stop());
    micStream = null;
  }
  if (micSource) {
    micSource.disconnect();
    micSource = null;
  }
  if (audioCtx) {
    audioCtx.close();
    audioCtx = null;
  }
  analyser = null;
}

// ─── Waveform Visualization ───
function startVisualization() {
  canvas = document.getElementById('voice-waveform');
  if (!canvas) return;
  canvasCtx = canvas.getContext('2d');
  
  // Set canvas resolution
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  canvasCtx.scale(dpr, dpr);
  
  drawFrame();
}

function stopVisualization() {
  if (animationFrame) {
    cancelAnimationFrame(animationFrame);
    animationFrame = null;
  }
}

function drawFrame() {
  animationFrame = requestAnimationFrame(drawFrame);
  
  if (!canvas || !canvasCtx) return;
  
  const W = canvas.getBoundingClientRect().width;
  const H = canvas.getBoundingClientRect().height;
  const cx = W / 2;
  const cy = H / 2;
  
  canvasCtx.clearRect(0, 0, W, H);
  
  // Get audio data
  let dataArray;
  let avgLevel = 0;
  
  if (analyser) {
    dataArray = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(dataArray);
    avgLevel = dataArray.reduce((a, b) => a + b, 0) / dataArray.length / 255;
  }
  
  const state = voiceState.state;
  const time = Date.now() / 1000;
  
  // Base radius
  const baseR = Math.min(W, H) * 0.25;
  
  // Colors by state
  const colors = {
    [STATES.IDLE]: { r: 100, g: 100, b: 130, glow: 'rgba(100,100,130,0.3)' },
    [STATES.LISTENING]: { r: 56, g: 210, b: 210, glow: 'rgba(56,210,210,0.4)' },
    [STATES.PROCESSING]: { r: 245, g: 180, b: 60, glow: 'rgba(245,180,60,0.4)' },
    [STATES.SPEAKING]: { r: 160, g: 100, b: 245, glow: 'rgba(160,100,245,0.4)' },
  };
  
  const color = colors[state] || colors[STATES.IDLE];
  
  // Draw outer glow
  const glowR = baseR + 30 + (avgLevel * 40);
  const gradient = canvasCtx.createRadialGradient(cx, cy, baseR * 0.5, cx, cy, glowR);
  gradient.addColorStop(0, `rgba(${color.r},${color.g},${color.b},0.15)`);
  gradient.addColorStop(1, 'rgba(0,0,0,0)');
  canvasCtx.fillStyle = gradient;
  canvasCtx.fillRect(0, 0, W, H);
  
  // Draw ring with audio-reactive deformation
  canvasCtx.beginPath();
  const points = 128;
  for (let i = 0; i <= points; i++) {
    const angle = (i / points) * Math.PI * 2;
    let r = baseR;
    
    if (state === STATES.LISTENING && dataArray) {
      const idx = Math.floor((i / points) * dataArray.length);
      r += (dataArray[idx] / 255) * 30;
    } else if (state === STATES.SPEAKING) {
      r += Math.sin(angle * 6 + time * 4) * 8 + Math.sin(angle * 3 + time * 2) * 5;
    } else if (state === STATES.PROCESSING) {
      r += Math.sin(angle * 4 + time * 3) * 6;
    } else {
      r += Math.sin(angle * 3 + time * 1.5) * 3;
    }
    
    const x = cx + Math.cos(angle) * r;
    const y = cy + Math.sin(angle) * r;
    
    if (i === 0) canvasCtx.moveTo(x, y);
    else canvasCtx.lineTo(x, y);
  }
  canvasCtx.closePath();
  
  // Fill
  const fillGrad = canvasCtx.createRadialGradient(cx, cy, 0, cx, cy, baseR);
  fillGrad.addColorStop(0, `rgba(${color.r},${color.g},${color.b},0.08)`);
  fillGrad.addColorStop(1, `rgba(${color.r},${color.g},${color.b},0.02)`);
  canvasCtx.fillStyle = fillGrad;
  canvasCtx.fill();
  
  // Stroke
  canvasCtx.strokeStyle = `rgba(${color.r},${color.g},${color.b},0.7)`;
  canvasCtx.lineWidth = 2;
  canvasCtx.stroke();
  
  // Inner pulsing circle
  const pulseR = baseR * 0.15 + Math.sin(time * 2) * 3 + avgLevel * 15;
  canvasCtx.beginPath();
  canvasCtx.arc(cx, cy, pulseR, 0, Math.PI * 2);
  canvasCtx.fillStyle = `rgba(${color.r},${color.g},${color.b},0.3)`;
  canvasCtx.fill();
  
  // Processing dots
  if (state === STATES.PROCESSING) {
    for (let i = 0; i < 3; i++) {
      const dotAngle = time * 3 + (i * Math.PI * 2 / 3);
      const dotR = baseR * 0.6;
      const dx = cx + Math.cos(dotAngle) * dotR;
      const dy = cy + Math.sin(dotAngle) * dotR;
      canvasCtx.beginPath();
      canvasCtx.arc(dx, dy, 4, 0, Math.PI * 2);
      canvasCtx.fillStyle = `rgba(${color.r},${color.g},${color.b},0.8)`;
      canvasCtx.fill();
    }
  }
}

// ─── Debug Log (visible in UI during testing) ───
let _voiceDebugLog = [];
function vlog(msg) {
  console.log('[VoiceMode]', msg);
  _voiceDebugLog.push(`${new Date().toLocaleTimeString()}: ${msg}`);
  if (_voiceDebugLog.length > 10) _voiceDebugLog.shift();
  const el = document.querySelector('.voice-debug');
  if (el) el.textContent = _voiceDebugLog.join('\n');
}

// ─── Speech Recognition ───
function startListening() {
  if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
    voiceState.error = 'Speech recognition not supported in this browser. Try Chrome.';
    render();
    return;
  }
  
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  recognition = new SpeechRecognition();
  
  // Mobile detection — use non-continuous mode for better reliability
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  recognition.continuous = !isMobile; // continuous on desktop, single-shot on mobile
  recognition.interimResults = true;
  recognition.lang = 'en-US';
  
  let lastResultTime = 0;
  let lastInterimText = '';
  
  recognition.onresult = (event) => {
    let interim = '';
    let final = '';
    
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const t = event.results[i][0].transcript;
      if (event.results[i].isFinal) {
        final += t + ' ';
      } else {
        interim += t;
      }
    }
    
    if (final) {
      voiceState.transcript += final;
    }
    voiceState.liveTranscript = voiceState.transcript + interim;
    lastResultTime = Date.now();
    lastInterimText = interim;
    vlog(`Result: final="${final.trim()}" interim="${interim.substring(0,30)}" transcript="${voiceState.transcript.substring(0,30)}"`);
    render();
    
    // Set silence timeout — fires when user stops speaking
    clearTimeout(recognitionTimeout);
    const hasText = voiceState.transcript.trim() || voiceState.liveTranscript.trim();
    if (hasText) {
      recognitionTimeout = setTimeout(() => {
        if (voiceState.state !== STATES.LISTENING) return;
        // Use whatever text we have — final or interim
        const textToSend = voiceState.transcript.trim() || voiceState.liveTranscript.trim();
        if (textToSend) {
          stopListening();
          processMessage(textToSend);
        }
      }, isMobile ? 1200 : 1500);
    }
  };
  
  // When recognition ends (mobile does this after each phrase)
  recognition.onend = () => {
    vlog(`onend: state=${voiceState.state} active=${voiceState.active} transcript="${voiceState.transcript.substring(0,30)}" live="${voiceState.liveTranscript.substring(0,30)}"`);
    if (voiceState.state === STATES.LISTENING && voiceState.active) {
      const text = voiceState.transcript.trim() || voiceState.liveTranscript.trim();
      if (text) {
        // We have text — send it
        vlog(`onend: sending "${text.substring(0,40)}"`);
        clearTimeout(recognitionTimeout);
        processMessage(text);
      } else if (voiceState.active) {
        // No text captured — restart listening
        vlog('onend: no text, restarting...');
        try { 
          recognition.start(); 
        } catch(e) { 
          vlog(`onend: restart failed: ${e.message}`);
          setState(STATES.IDLE); 
        }
      }
    }
  };
  
  recognition.onerror = (event) => {
    vlog(`onerror: ${event.error}`);
    if (event.error === 'not-allowed') {
      voiceState.error = 'Microphone access denied. Please allow mic access in your browser settings.';
      setState(STATES.IDLE);
    } else if (event.error === 'no-speech' || event.error === 'aborted') {
      // Non-fatal — onend will handle restart or send
      return;
    }
    // For other errors (audio-capture, network, etc.) — DON'T change state
    // Let onend handle it — it will try to send text or restart
  };
    
  voiceState.transcript = '';
  voiceState.liveTranscript = '';
  setState(STATES.LISTENING);
  
  try {
    recognition.start();
    vlog(`Recognition started (continuous: ${recognition.continuous})`);
  } catch(e) {
    vlog(`Failed to start: ${e.message}`);
    voiceState.error = 'Failed to start voice recognition. Try again.';
    setState(STATES.IDLE);
  }
}

function stopListening() {
  clearTimeout(recognitionTimeout);
  if (recognition) {
    try { recognition.stop(); } catch(e) {}
    recognition = null;
  }
}

// ─── Process & Send Message ───
async function processMessage(text) {
  vlog(`processMessage: "${text.substring(0,50)}"`);
  setState(STATES.PROCESSING);
  
  // Add to history
  voiceState.history.push({ role: 'user', text });
  render();
  
  // Send message to the active agent or chat
  const tabId = getActiveAgentTab();
  
  try {
    if (isAgentTab(tabId)) {
      vlog('Sending to agent: ' + tabId);
      const sendPromise = sendAgentMessage(tabId, text);
      await streamAndSpeak(tabId, 'agent');
      await sendPromise.catch(() => {});
    } else {
      vlog('Sending to chat tab');
      const sendPromise = sendMessage(text);
      await streamAndSpeak(tabId, 'chat');
      await sendPromise.catch(() => {});
    }
    
  } catch (err) {
    vlog('ERROR: ' + err.message);
    voiceState.error = `Error: ${err.message}. Tap circle to try again.`;
    setState(STATES.IDLE);
    render();
    return;
  }
  
  // Auto-continue: start listening again
  if (voiceState.active) {
    voiceState.transcript = '';
    voiceState.liveTranscript = '';
    startListening();
  }
}

// Stream-and-speak: watch the AI response, send to streaming TTS
async function streamAndSpeak(tabId, mode) {
  const startTime = Date.now();
  const maxWait = 60000;
  let spokenLength = 0;
  let fullResponse = '';
  
  // Speech queue with parallel playback
  const speechQueue = [];
  let speakerRunning = false;
  let streamDone = false;
  
  // Speaker loop: plays audio chunks sequentially
  const speakerPromise = new Promise(resolve => {
    async function speakerLoop() {
      speakerRunning = true;
      while (true) {
        if (speechQueue.length > 0) {
          const chunk = speechQueue.shift();
          vlog('🔊 ' + chunk.substring(0, 50));
          await speakText(chunk);
        } else if (streamDone) {
          break;
        } else {
          await new Promise(r => setTimeout(r, 80));
        }
      }
      speakerRunning = false;
      resolve();
    }
    speakerLoop();
  });
  
  return new Promise((resolve, reject) => {
    let lastQueueTime = 0;
    
    const check = setInterval(() => {
      const s = store.getState();
      let lastAssistant = null;
      
      if (mode === 'agent') {
        const c = s.agentConversations?.[tabId];
        const msgs = c?.messages || [];
        lastAssistant = [...msgs].reverse().find(m => m.role === 'assistant');
      } else {
        const cid = s.chat?.currentConversationId;
        const c = s.conversations?.find?.(x => x.id === cid);
        const msgs = c?.messages || [];
        lastAssistant = [...msgs].reverse().find(m => m.role === 'assistant');
      }
      
      if (!lastAssistant || !lastAssistant.content) {
        if (Date.now() - startTime > maxWait) {
          clearInterval(check); streamDone = true;
          reject(new Error('Response timeout'));
        }
        return;
      }
      
      const msgTime = lastAssistant.timestamp ? new Date(lastAssistant.timestamp).getTime() : 0;
      if (msgTime < startTime - 2000 && lastAssistant.timestamp) return;
      
      const currentText = lastAssistant.content;
      const isComplete = mode === 'agent' 
        ? lastAssistant.status !== 'streaming'
        : lastAssistant.status === 'complete';
      
      if (currentText.length > spokenLength) {
        const newText = currentText.substring(spokenLength);
        const now = Date.now();
        
        if (isComplete) {
          // Response done — send ALL remaining text
          if (newText.trim()) {
            speechQueue.push(newText.trim());
            spokenLength = currentText.length;
          }
        } else {
          // During streaming: queue text at natural sentence boundaries
          // Look for sentence endings to get meaningful chunks
          const sentenceEnd = findLastSentenceEnd(newText);
          
          if (sentenceEnd > 0) {
            // Found a complete sentence — queue it
            const chunk = newText.substring(0, sentenceEnd).trim();
            if (chunk) {
              speechQueue.push(chunk);
              spokenLength += sentenceEnd;
              lastQueueTime = now;
            }
          } else if (newText.length > 120 && now - lastQueueTime > 2000) {
            // Long text, no sentence boundary, been waiting 2s — force queue at clause
            const clauseEnd = findLastClauseEnd(newText);
            if (clauseEnd > 30) {
              const chunk = newText.substring(0, clauseEnd).trim();
              if (chunk) {
                speechQueue.push(chunk);
                spokenLength += clauseEnd;
                lastQueueTime = now;
              }
            }
          }
        }
      }
      
      if (isComplete && currentText.length <= spokenLength) {
        clearInterval(check);
        streamDone = true;
        fullResponse = currentText;
        
        speakerPromise.then(() => {
          voiceState.agentResponse = fullResponse;
          voiceState.history.push({ role: 'assistant', text: fullResponse });
          vlog('✅ Done');
          resolve(fullResponse);
        });
      }
      
      if (Date.now() - startTime > maxWait) {
        clearInterval(check); streamDone = true;
        reject(new Error('Response timeout'));
      }
    }, 150);
  });
}

// Find the last sentence-ending position (after . ! ?)
function findLastSentenceEnd(text) {
  const match = text.match(/.*[.!?]\s/s);
  return match ? match[0].length : -1;
}

// Find the last clause-ending position (after , ; : — or sentence end)
function findLastClauseEnd(text) {
  const match = text.match(/.*[.!?,;:\u2014–—]\s/s);
  return match ? match[0].length : -1;
}

function waitForAgentResponse(agentId) {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    const maxWait = 60000;
    
    const check = setInterval(() => {
      const s = store.getState();
      const c = s.agentConversations?.[agentId];
      const msgs = c?.messages || [];
      
      // Find the last assistant message — must be streaming or just completed
      const lastAssistant = [...msgs].reverse().find(m => m.role === 'assistant');
      if (lastAssistant && lastAssistant.content && lastAssistant.status !== 'streaming') {
        // Check it was created AFTER we started waiting (within last 60s)
        const msgTime = lastAssistant.timestamp ? new Date(lastAssistant.timestamp).getTime() : 0;
        if (msgTime >= startTime - 2000 || !lastAssistant.timestamp) {
          clearInterval(check);
          resolve(lastAssistant.content);
          return;
        }
      }
      
      if (Date.now() - startTime > maxWait) {
        clearInterval(check);
        reject(new Error('Response timeout'));
      }
    }, 300);
  });
}

function waitForChatResponse() {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    const maxWait = 60000;
    
    // Wait a moment for sendMessage to add the user msg + streaming placeholder
    setTimeout(() => {
      const check = setInterval(() => {
        const s = store.getState();
        const cid = s.chat?.currentConversationId;
        const c = s.conversations?.find?.(x => x.id === cid);
        const msgs = c?.messages || [];
        
        // Find a streaming assistant message that completed
        const lastAssistant = [...msgs].reverse().find(m => m.role === 'assistant');
        if (lastAssistant && lastAssistant.content && lastAssistant.status === 'complete') {
          // Verify it's recent — created after we started
          const msgTime = lastAssistant.timestamp ? new Date(lastAssistant.timestamp).getTime() : 0;
          if (msgTime >= startTime - 2000 || !lastAssistant.timestamp) {
            clearInterval(check);
            resolve(lastAssistant.content);
            return;
          }
        }
        
        if (Date.now() - startTime > maxWait) {
          clearInterval(check);
          // Try to get whatever streaming content we have
          if (lastAssistant && lastAssistant.content) {
            resolve(lastAssistant.content);
          } else {
            reject(new Error('Response timeout'));
          }
        }
      }, 300);
    }, 500); // Wait 500ms for message to be added to store
  });
}

// ─── Text-to-Speech ───
// ─── Audio playback ───
let currentAudio = null;

async function speakText(text) {
  setState(STATES.SPEAKING);
  
  if (!text?.trim()) return;
  
  // Try server TTS (Piper on VPS), fall back to browser TTS
  try {
    const res = await fetch(`${API_BASE}/api/voice/tts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    
    if (res.ok) {
      const contentType = res.headers.get('content-type') || '';
      
      if (contentType.includes('audio/')) {
        // Binary WAV response from Piper TTS
        const audioBlob = await res.blob();
        vlog(`Playing Piper TTS (${(audioBlob.size / 1024).toFixed(0)}KB)`);
        await playAudioBlob(audioBlob);
        return;
      }
      
      // JSON response (legacy Gemini format)
      const data = await res.json();
      if (data.ok && data.audioBase64) {
        vlog('Playing base64 TTS');
        await playBase64Audio(data.audioBase64, data.mimeType || 'audio/wav');
        return;
      } else {
        vlog('TTS API error: ' + (data.error || 'unknown'));
      }
    } else {
      vlog('TTS HTTP ' + res.status);
    }
    vlog('Server TTS failed, falling back to browser');
  } catch (err) {
    vlog(`TTS fetch error: ${err.message}`);
  }
  
  // Fallback: browser TTS
  await browserSpeak(text);
}

function playAudioBlob(blob) {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(blob);
      currentAudio = new Audio(url);
      currentAudio.onended = () => { URL.revokeObjectURL(url); currentAudio = null; resolve(); };
      currentAudio.onerror = () => { URL.revokeObjectURL(url); currentAudio = null; resolve(); };
      currentAudio.play().catch(() => resolve());
    } catch (e) {
      vlog(`Blob playback error: ${e.message}`);
      resolve();
    }
  });
}

function playBase64Audio(base64Data, mimeType) {
  return new Promise((resolve) => {
    try {
      // Convert base64 to binary
      const binaryStr = atob(base64Data);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) {
        bytes[i] = binaryStr.charCodeAt(i);
      }
      
      // PCM L16 needs to be wrapped in WAV for Audio element
      if (mimeType && mimeType.includes('L16')) {
        // Extract sample rate from mimeType (e.g., "audio/L16;codec=pcm;rate=24000")
        const rateMatch = mimeType.match(/rate=(\d+)/);
        const sampleRate = rateMatch ? parseInt(rateMatch[1]) : 24000;
        const wavBytes = pcmToWav(bytes, sampleRate);
        const blob = new Blob([wavBytes], { type: 'audio/wav' });
        const url = URL.createObjectURL(blob);
        
        currentAudio = new Audio(url);
        currentAudio.onended = () => { URL.revokeObjectURL(url); currentAudio = null; resolve(); };
        currentAudio.onerror = () => { URL.revokeObjectURL(url); currentAudio = null; resolve(); };
        currentAudio.play().catch(() => resolve());
      } else {
        // Regular audio format
        const blob = new Blob([bytes], { type: mimeType || 'audio/mp3' });
        const url = URL.createObjectURL(blob);
        currentAudio = new Audio(url);
        currentAudio.onended = () => { URL.revokeObjectURL(url); currentAudio = null; resolve(); };
        currentAudio.onerror = () => { URL.revokeObjectURL(url); currentAudio = null; resolve(); };
        currentAudio.play().catch(() => resolve());
      }
    } catch (e) {
      vlog(`Audio playback error: ${e.message}`);
      resolve();
    }
  });
}

// Convert raw PCM L16 to WAV format
function pcmToWav(pcmData, sampleRate) {
  const numChannels = 1;
  const bitsPerSample = 16;
  const byteRate = sampleRate * numChannels * (bitsPerSample / 8);
  const blockAlign = numChannels * (bitsPerSample / 8);
  const dataSize = pcmData.length;
  const headerSize = 44;
  const buffer = new ArrayBuffer(headerSize + dataSize);
  const view = new DataView(buffer);
  
  // WAV header
  const writeString = (offset, str) => { for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i)); };
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // PCM format chunk size
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);
  
  // Copy PCM data
  const output = new Uint8Array(buffer);
  output.set(pcmData, headerSize);
  
  return buffer;
}

function playHexAudio(hexString, format) {
  return new Promise((resolve) => {
    try {
      // Convert hex to binary
      const bytes = new Uint8Array(hexString.length / 2);
      for (let i = 0; i < hexString.length; i += 2) {
        bytes[i / 2] = parseInt(hexString.substr(i, 2), 16);
      }
      
      const blob = new Blob([bytes], { type: `audio/${format}` });
      const url = URL.createObjectURL(blob);
      
      currentAudio = new Audio(url);
      currentAudio.onended = () => {
        URL.revokeObjectURL(url);
        currentAudio = null;
        resolve();
      };
      currentAudio.onerror = () => {
        URL.revokeObjectURL(url);
        currentAudio = null;
        resolve();
      };
      currentAudio.play().catch(() => resolve());
    } catch (e) {
      vlog(`Audio playback error: ${e.message}`);
      resolve();
    }
  });
}

function browserSpeak(text) {
  return new Promise((resolve) => {
    const cleanText = text
      .replace(/```[\s\S]*?```/g, 'code block')
      .replace(/`[^`]+`/g, (m) => m.replace(/`/g, ''))
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/#{1,6}\s/g, '')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/[|─┌┐└┘┬┴├┤]/g, '')
      .replace(/\n{2,}/g, '. ')
      .replace(/\n/g, ' ')
      .trim();
    
    if (!cleanText) { resolve(); return; }
    
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.rate = 1.05;
    utterance.pitch = 1.0;
    
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(v => 
      v.name.includes('Samantha') || v.name.includes('Google UK English Female') ||
      v.name.includes('Microsoft Zira') || (v.lang === 'en-US' && v.name.includes('Female'))
    ) || voices.find(v => v.lang.startsWith('en')) || voices[0];
    if (preferred) utterance.voice = preferred;
    
    utterance.onend = () => resolve();
    utterance.onerror = () => resolve();
    window.speechSynthesis.speak(utterance);
  });
}

function stopSpeaking() {
  window.speechSynthesis.cancel();
  if (currentAudio) {
    currentAudio.pause();
    currentAudio = null;
  }
}

// ─── State Management ───
function setState(newState) {
  voiceState.state = newState;
  voiceState.error = null;
  render();
}

// ─── Interrupt ───
function interrupt() {
  if (voiceState.state === STATES.SPEAKING) {
    stopSpeaking();
    voiceState.transcript = '';
    voiceState.liveTranscript = '';
    startListening();
  }
}

// ─── Render ───
function render() {
  const el = document.getElementById('voice-mode-overlay');
  if (!el) return;
  
  // Update state indicator
  const stateLabel = el.querySelector('.voice-state-label');
  if (stateLabel) {
    const labels = {
      [STATES.IDLE]: 'Ready',
      [STATES.LISTENING]: 'Listening...',
      [STATES.PROCESSING]: 'Thinking...',
      [STATES.SPEAKING]: 'Speaking...',
    };
    stateLabel.textContent = labels[voiceState.state] || '';
    stateLabel.dataset.state = voiceState.state;
  }
  
  // Update transcript
  const transcriptEl = el.querySelector('.voice-transcript');
  if (transcriptEl) {
    const display = voiceState.liveTranscript || voiceState.transcript;
    if (voiceState.state === STATES.IDLE && !display) {
      transcriptEl.textContent = 'Tap the circle or press Space to start';
      transcriptEl.style.opacity = '0.3';
    } else if (voiceState.state === STATES.PROCESSING) {
      transcriptEl.textContent = `"${display}"`;
      transcriptEl.style.opacity = '0.7';
    } else {
      transcriptEl.textContent = display || (voiceState.state === STATES.LISTENING ? 'Speak now...' : '');
      transcriptEl.style.opacity = display ? '1' : '0.3';
    }
  }
  
  // Update response
  const responseEl = el.querySelector('.voice-response');
  if (responseEl) {
    const lastAssistant = [...voiceState.history].reverse().find(h => h.role === 'assistant');
    responseEl.textContent = lastAssistant?.text?.substring(0, 200) || '';
  }
  
  // Update error
  const errorEl = el.querySelector('.voice-error');
  if (errorEl) {
    errorEl.textContent = voiceState.error || '';
    errorEl.style.display = voiceState.error ? 'block' : 'none';
  }
  
  // Update history
  const historyEl = el.querySelector('.voice-history');
  if (historyEl) {
    historyEl.innerHTML = voiceState.history.map(h => `
      <div class="voice-history-item voice-history-${h.role}">
        <span class="voice-history-role">${h.role === 'user' ? '🎤' : '🤖'}</span>
        <span class="voice-history-text">${escapeForHtml(h.text.substring(0, 150))}${h.text.length > 150 ? '...' : ''}</span>
      </div>
    `).join('');
    historyEl.scrollTop = historyEl.scrollHeight;
  }
}

function escapeForHtml(text) {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// ─── Public API ───

export function openVoiceMode() {
  voiceState = {
    active: true,
    state: STATES.IDLE,
    transcript: '',
    liveTranscript: '',
    agentResponse: '',
    history: [],
    error: null,
  };
  
  // Get current agent name
  const tabId = getActiveAgentTab();
  const state = store.getState();
  let agentName = 'Chorus AI';
  let agentEmoji = '🤖';
  
  if (isAgentTab(tabId)) {
    const providers = state.providers || {};
    const ocModels = providers.openclaw?.models || [];
    const model = ocModels.find(m => m.id === tabId);
    agentName = model?.name || tabId;
    agentEmoji = '🦞';
  }
  
  // Create overlay
  const overlay = document.createElement('div');
  overlay.id = 'voice-mode-overlay';
  overlay.className = 'voice-overlay';
  overlay.innerHTML = `
    <div class="voice-container" onclick="event.stopPropagation()">
      <div class="voice-header">
        <span class="voice-agent-emoji">${agentEmoji}</span>
        <span class="voice-agent-name">${escapeForHtml(agentName)}</span>
        <span class="voice-state-label" data-state="idle">Ready</span>
      </div>
      
      <div class="voice-canvas-wrap" id="voice-canvas-tap">
        <canvas id="voice-waveform"></canvas>
      </div>
      
      <div class="voice-transcript-area">
        <div class="voice-transcript" style="opacity:0.3">Tap the circle or press Space to start</div>
        <div class="voice-response"></div>
      </div>
      
      <div class="voice-error" style="display:none"></div>
      
      <div class="voice-history"></div>
      
      <div class="voice-controls">
        <button class="voice-btn voice-btn-mute" id="voice-mute" title="Mute">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
        </button>
        <button class="voice-btn voice-btn-end" id="voice-end" title="End">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
        </button>
        <button class="voice-btn voice-btn-settings" id="voice-settings" title="Settings">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
        </button>
      </div>
    </div>
  `;
  
  document.body.appendChild(overlay);
  
  // Bind events
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeVoiceMode();
  });
  
  document.getElementById('voice-canvas-tap').addEventListener('click', () => {
    if (voiceState.state === STATES.IDLE) {
      // Start speech recognition FIRST (needs mic), then setup audio for visualization
      startListening();
      startVisualization();
      // Audio setup for waveform is optional — do it after recognition starts
      setupAudio().catch(() => {});
    } else if (voiceState.state === STATES.SPEAKING) {
      interrupt();
    } else if (voiceState.state === STATES.LISTENING) {
      // Manual stop → send what we have (check both final and interim transcript)
      stopListening();
      const textToSend = voiceState.transcript.trim() || voiceState.liveTranscript.trim();
      if (textToSend) {
        processMessage(textToSend);
      } else {
        setState(STATES.IDLE);
      }
    }
  });
  
  document.getElementById('voice-end').addEventListener('click', closeVoiceMode);
  
  document.getElementById('voice-mute').addEventListener('click', () => {
    if (voiceState.state === STATES.LISTENING) {
      stopListening();
      setState(STATES.IDLE);
    } else if (voiceState.state === STATES.IDLE) {
      startListening();
      startVisualization();
      setupAudio().catch(() => {});
    }
  });
  
  // Keyboard: Space to toggle, Escape to close
  const keyHandler = (e) => {
    if (e.code === 'Space' && !e.target.closest('input, textarea')) {
      e.preventDefault();
      if (voiceState.state === STATES.IDLE) {
        startListening();
        startVisualization();
        setupAudio().catch(() => {});
      } else if (voiceState.state === STATES.LISTENING) {
        stopListening();
        const textToSend = voiceState.transcript.trim() || voiceState.liveTranscript.trim();
        if (textToSend) {
          processMessage(textToSend);
        } else {
          setState(STATES.IDLE);
        }
      } else if (voiceState.state === STATES.SPEAKING) {
        interrupt();
      }
    } else if (e.code === 'Escape') {
      closeVoiceMode();
    }
  };
  document.addEventListener('keydown', keyHandler);
  overlay._keyHandler = keyHandler;
  
  // Request wake lock to prevent screen sleep
  requestWakeLock();
  
  // Start visualization (even without audio, for the idle animation)
  setTimeout(() => startVisualization(), 100);
}

export function closeVoiceMode() {
  voiceState.active = false;
  stopListening();
  stopSpeaking();
  stopVisualization();
  teardownAudio();
  releaseWakeLock();
  
  const overlay = document.getElementById('voice-mode-overlay');
  if (overlay) {
    if (overlay._keyHandler) {
      document.removeEventListener('keydown', overlay._keyHandler);
    }
    overlay.remove();
  }
}

export function isVoiceModeActive() {
  return voiceState.active;
}

// ─── Wake Lock (prevent screen sleep) ───
let wakeLock = null;

async function requestWakeLock() {
  try {
    if ('wakeLock' in navigator) {
      wakeLock = await navigator.wakeLock.request('screen');
    }
  } catch (e) {
    // Wake lock not supported or denied
  }
}

function releaseWakeLock() {
  if (wakeLock) {
    wakeLock.release();
    wakeLock = null;
  }
}
